<script setup lang="ts">
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import type { Stats4Props } from "./stats4Props";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<Stats4Props>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        class="relative isolate overflow-hidden"
        :scroll-animation-type="scrollAnimationType"
    >
        <Image
            v-if="!dataValues.backgroundImage.isHidden"
            :src="dataValues.backgroundImage.data.src"
            alt=""
            :style="{
                opacity: dataValues.backgroundImage.data.transparency / 100,
            }"
            class="absolute inset-0 -z-10 size-full object-cover mix-blend-multiply"
        />
        <FullSpacing>
            <div class="relative mx-auto max-w-7xl flex flex-col gap-y-16 sm:gap-y-20">
                <div
                    v-if="
                        !dataValues.topLabel.isHidden ||
                        !dataValues.title.isHidden ||
                        !dataValues.subtitle.isHidden
                    "
                    class="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl flex flex-col gap-y-2"
                >
                    <EditableElement
                        v-if="!dataValues.topLabel.isHidden"
                        :block-id="block.id"
                        field-name="topLabel"
                    >
                        <Heading
                            :text="dataValues.topLabel.data.text"
                            :tag="dataValues.topLabel.data.tag"
                            :text-color="styleValues.text"
                            class="text-base/8 opacity-70"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :block-id="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.subtitle.isHidden"
                        :block-id="block.id"
                        field-name="subtitle"
                    >
                        <Paragraph
                            :text="dataValues.subtitle.data.text"
                            :text-color="styleValues.text"
                            class="text-lg/8 opacity-70"
                        />
                    </EditableElement>
                </div>
                <dl
                    v-if="dataValues.stats.data.items.length > 0"
                    class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 sm:gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-4"
                >
                    <div
                        class="flex flex-col gap-y-3"
                        v-for="stat in dataValues.stats.data.items"
                        :key="`stat${stat.id}`"
                        :style="{
                            color: styleValues.text,
                            borderColor: styleValues.border,
                        }"
                    >
                        <EditableElement
                            :block-id="block.id"
                            :field-name="`stats_${stat.id}`"
                            :hideable="false"
                        >
                            <dt class="text-sm/6 opacity-70">
                                {{ stat.statDesc }}
                            </dt>
                            <dd class="order-first text-3xl font-semibold tracking-tight">
                                {{ stat.statTitle }}
                            </dd>
                        </EditableElement>
                    </div>
                </dl>
            </div>
        </FullSpacing>
    </Section>
</template>
